// utils/safe-link-path.ts — FP8 deep-link allow-list.
//
// System nudges (and, in future, any server-supplied message link) carry a
// `link_path`. Before we navigate, we validate it against an allow-list of
// known internal route prefixes so a malformed or hostile value can never
// drive the SPA router somewhere unexpected (open-redirect / external nav /
// javascript: scheme). We only ever navigate to in-app paths.

/** Internal route prefixes a message CTA is allowed to target. */
const ALLOWED_PREFIXES = [
  '/prof',
  '/simula',
  '/progresso',
  '/appendice',
  '/messaggi',
  '/studia/argomento/',
  '/studia/lezione/',
] as const;

/**
 * Returns the path if it is a safe in-app deep link, else null.
 * - Must be a non-empty string starting with a single '/'.
 * - Must NOT start with '//' (protocol-relative external URL).
 * - Must match one of the allow-listed prefixes (exact for fixed routes,
 *   prefix for parameterized ones).
 */
export function safeLinkPath(path: string | null | undefined): string | null {
  if (typeof path !== 'string') return null;
  const p = path.trim();
  if (p.length === 0) return null;
  if (!p.startsWith('/')) return null;
  if (p.startsWith('//')) return null; // protocol-relative → external
  // Reject any control chars / whitespace / backslashes that could confuse the router.
  if (/[\s\\]/.test(p)) return null;
  for (const prefix of ALLOWED_PREFIXES) {
    if (prefix.endsWith('/')) {
      // Parameterized route: require something after the prefix.
      if (p.startsWith(prefix) && p.length > prefix.length) return p;
    } else if (p === prefix || p.startsWith(prefix + '/') || p.startsWith(prefix + '?')) {
      return p;
    }
  }
  return null;
}
